import { useRouter } from 'expo-router';
import {
  Baby,
  Briefcase,
  Building2,
  Droplet,
  Flame,
  GraduationCap,
  HandHelping,
  Headphones,
  Home,
  Key,
  Laptop,
  PawPrint,
  PersonStanding,
  Plane,
  Snowflake,
  Sprout,
  Sun,
  Tractor,
  Users,
  Webhook,
  Wind,
} from 'lucide-react-native';
import { ComponentType, useEffect } from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';

import { PrimaryCTA, SkipLink } from '@/components/onboarding/Controls';
import { Eyebrow, H1, Screen, WhyLine } from '@/components/onboarding/Screen';
import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';
import attributeRules from '@/data/attribute-rules';
import {
  REGION_HAZARD_LABEL,
  REGION_HAZARD_ORDER,
} from '@/data/region-hazards';
import type { RegionHazard } from '@/db/types';
import { useOnboarding } from '@/store/onboarding';

/**
 * Lifestyle step — pool / solar / pets / kids. Each adds a small set of
 * timely reminders to the plan. Skippable; defaults all false. Lives
 * between the property step and the optional detail step so the
 * generated plan accounts for these from day one.
 */
type LifeKey =
  | 'hasPool' | 'hasSolar' | 'hasPets' | 'hasKids'
  | 'hasHoa' | 'hasWoodfire' | 'hasSmartHome' | 'hasStrHost'
  | 'hasHeavySnow' | 'hasGarden' | 'hasAccessibility' | 'isFirstTime'
  | 'travelsOften' | 'worksFromHome' | 'hasAllergies' | 'hasNewborn'
  | 'multiGenHousehold' | 'over60Owner' | 'hasLivestock'
  | 'hasShortTermContractors';

type RuleField =
  | 'has_pool' | 'has_solar' | 'has_pets' | 'has_kids'
  | 'has_hoa' | 'has_woodfire' | 'has_smart_home' | 'has_str_host'
  | 'has_heavy_snow' | 'has_garden' | 'has_accessibility' | 'is_first_time'
  | 'travels_often' | 'works_from_home' | 'has_allergies' | 'has_newborn'
  | 'multi_gen_household' | 'over_60_owner' | 'has_livestock'
  | 'has_short_term_contractors';

interface LifeOption {
  key: LifeKey;
  ruleField: RuleField;
  title: string;
  sub: string;
  Icon: ComponentType<{ size?: number; color?: string; strokeWidth?: number }>;
  tint: string;
  bg: string;
}

const countFor = (field: RuleField): number =>
  attributeRules.filter((r) => (r.applies_to as Record<string, unknown>)[field] === true).length;

const OPTS: LifeOption[] = [
  { key: 'hasPool',          ruleField: 'has_pool',          title: 'Pool',                 sub: 'Weekly chemistry, summer maintenance, fall winterization',          Icon: Droplet,        tint: '#2A6F97',         bg: 'rgba(42,111,151,0.12)' },
  { key: 'hasSolar',         ruleField: 'has_solar',         title: 'Solar panels',         sub: 'Annual wash + monthly production check',                            Icon: Sun,            tint: Palette.honeyDeep, bg: 'rgba(225,163,61,0.18)' },
  { key: 'hasPets',          ruleField: 'has_pets',          title: 'Pets at home',         sub: 'Mid-cycle HVAC filter, register vacuum, yard cleanup',              Icon: PawPrint,       tint: Palette.clay,      bg: 'rgba(176,95,60,0.15)' },
  { key: 'hasKids',          ruleField: 'has_kids',          title: 'Kids under 12',        sub: 'Water-temp check, outlet covers, medicine cabinet audit',           Icon: GraduationCap,  tint: Palette.forest,    bg: '#EAF0EA' },
  { key: 'hasHoa',           ruleField: 'has_hoa',           title: 'HOA',                  sub: 'Dues check + an annual skim of architectural rules',                Icon: Building2,      tint: Palette.forest,    bg: '#EAF0EA' },
  { key: 'hasWoodfire',      ruleField: 'has_woodfire',      title: 'Wood stove / fireplace', sub: 'Annual chimney sweep, firewood stack, weekly ash safety',         Icon: Flame,          tint: Palette.clay,      bg: 'rgba(176,95,60,0.15)' },
  { key: 'hasSmartHome',     ruleField: 'has_smart_home',    title: 'Smart-home hub',       sub: 'Sensor-battery swap + quarterly firmware check',                    Icon: Webhook,        tint: Palette.forestLight, bg: '#EAF0EA' },
  { key: 'hasStrHost',       ruleField: 'has_str_host',      title: 'Short-term rental host', sub: 'Deep-clean cadence, supply restock, guest-safety alarm tests',  Icon: Briefcase,      tint: Palette.honeyDeep, bg: 'rgba(225,163,61,0.18)' },
  { key: 'hasHeavySnow',     ruleField: 'has_heavy_snow',    title: 'Heavy-snow region',    sub: 'Gutter prep, roof snow checks, blower pre-season test',             Icon: Snowflake,      tint: '#2A6F97',         bg: 'rgba(42,111,151,0.12)' },
  { key: 'hasGarden',        ruleField: 'has_garden',        title: 'Garden / vegetable beds', sub: 'Annual soil test, spring bed prep, fall cleanup',               Icon: Sprout,         tint: Palette.moss,      bg: '#EAF0EA' },
  { key: 'hasAccessibility', ruleField: 'has_accessibility', title: 'Accessibility / aging-in-place', sub: 'Grab-bar tests + quarterly fall-hazard walk',           Icon: HandHelping,    tint: Palette.forest,    bg: '#EAF0EA' },
  { key: 'isFirstTime',      ruleField: 'is_first_time',     title: 'First-time homeowner', sub: 'Extra setup guidance: shutoffs tour + document binder',             Icon: Home,           tint: Palette.honeyDeep, bg: 'rgba(225,163,61,0.18)' },
  // v35 lifestyle additions — each captures a real homeowner segment.
  { key: 'travelsOften',     ruleField: 'travels_often',     title: 'Travel often (7+ days at a time)', sub: 'Pre-trip checklist, leak-monitor armed, freeze risk',         Icon: Plane,          tint: Palette.forest,    bg: '#EAF0EA' },
  { key: 'worksFromHome',    ruleField: 'works_from_home',   title: 'Work from home',       sub: '2× HVAC duty — mid-cycle filter swap, indoor-air-quality focus',     Icon: Laptop,         tint: Palette.forest,    bg: '#EAF0EA' },
  { key: 'hasAllergies',     ruleField: 'has_allergies',     title: 'Allergies / asthma',   sub: 'MERV-13 upgrade hint, HEPA filter swap, duct-clean cadence',         Icon: Headphones,     tint: Palette.forestLight, bg: '#EAF0EA' },
  { key: 'hasNewborn',       ruleField: 'has_newborn',       title: 'Newborn / under 1',    sub: 'Humidity 40–60%, lead-check, smoke alarm proximity',                 Icon: Baby,           tint: Palette.honeyDeep, bg: 'rgba(225,163,61,0.18)' },
  { key: 'multiGenHousehold',ruleField: 'multi_gen_household', title: 'Multi-generational household', sub: 'In-law unit walk-through, extra water heater + zones',     Icon: Users,          tint: Palette.forest,    bg: '#EAF0EA' },
  { key: 'over60Owner',      ruleField: 'over_60_owner',     title: 'Owner over 60',        sub: 'Skip-the-ladder nudges; handrail + nightlight checks',               Icon: PersonStanding, tint: Palette.forest,    bg: '#EAF0EA' },
  { key: 'hasLivestock',     ruleField: 'has_livestock',     title: 'Livestock or horses',  sub: 'Trough heater, barn vent, fence walk, feed-pest control',            Icon: Tractor,        tint: Palette.clay,      bg: 'rgba(176,95,60,0.15)' },
  { key: 'hasShortTermContractors', ruleField: 'has_short_term_contractors', title: 'Frequent contractors (cleaner, lawn, pool)', sub: 'Access-code rotate, lockbox battery, alarm-code hygiene', Icon: Key, tint: Palette.honeyDeep, bg: 'rgba(225,163,61,0.18)' },
];

// Count of region_hazards rules that apply to a given hazard tag —
// drives the "+N tasks" hint on each chip so users see the cost of
// turning one on.
const HAZARD_RULE_COUNTS: Record<RegionHazard, number> = (() => {
  // Lazy require to avoid a top-level JSON load slowing first paint of
  // unrelated screens. attributeRules above is the typed library; the
  // storm rules live in the JSON file alongside the other system rules.
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const taskRules = require('@/data/task-rules.json') as Array<{
    applies_to?: { region_hazards?: RegionHazard[] };
  }>;
  const out: Record<RegionHazard, number> = {
    hurricane: 0,
    tornado: 0,
    wildfire: 0,
    earthquake: 0,
    flood: 0,
    'ice-storm': 0,
  };
  for (const r of taskRules) {
    const hz = r.applies_to?.region_hazards;
    if (!hz) continue;
    for (const h of hz) out[h] = (out[h] ?? 0) + 1;
  }
  return out;
})();

export default function LifestyleStep() {
  const router = useRouter();
  const state = useOnboarding();
  const setLifestyle = useOnboarding((s) => s.setLifestyle);
  const toggleRegionHazard = useOnboarding((s) => s.toggleRegionHazard);
  const applyRegionHazardDefaults = useOnboarding(
    (s) => s.applyRegionHazardDefaults
  );

  // Pre-fill hazard chips from the ZIP the user entered earlier.
  // applyRegionHazardDefaults() no-ops if the user has already touched
  // the chips, so back-nav doesn't clobber their explicit choice.
  useEffect(() => {
    applyRegionHazardDefaults();
  }, [applyRegionHazardDefaults]);

  const next = () => {
    const dest =
      state.mode === 'full' ? '/(onboarding)/detail' : '/(onboarding)/generating';
    router.push(dest as never);
  };

  // Live running tally of how many extra reminders the selections add.
  const totalExtra = OPTS.reduce(
    (sum, o) => (state[o.key] ? sum + countFor(o.ruleField) : sum),
    0
  );

  return (
    <Screen
      scroll
      footer={
        <>
          <PrimaryCTA label="Continue" onPress={next} />
          <SkipLink label="None of these" onPress={next} />
        </>
      }>
      <Eyebrow>8 · Your life</Eyebrow>
      <H1>Anything else we should know?</H1>
      <WhyLine>
        Toggle anything that applies. Each adds a small set of timely reminders —
        skip them all and you can flip these later in Edit my home.
      </WhyLine>

      {/*
        Bulk-toggle row — handy for users who want to see "everything"
        their property could possibly track, then turn off the ones
        that don't apply. Kept visually small (single row, plain text,
        no big button) so it doesn't compete with the per-row toggles.
      */}
      {(() => {
        const allOn = OPTS.every((o) => state[o.key]);
        return (
          <Pressable
            onPress={() => {
              const target = !allOn;
              for (const o of OPTS) setLifestyle(o.key, target);
            }}
            style={({ pressed }) => [
              styles.selectAll,
              pressed && { opacity: 0.75 },
            ]}
            accessibilityRole="button"
            accessibilityLabel={allOn ? 'Deselect all lifestyle flags' : 'Select all lifestyle flags'}>
            <Text style={styles.selectAllText}>
              {allOn ? '✓ All selected — tap to clear' : 'Select all'}
            </Text>
          </Pressable>
        );
      })()}

      <View style={{ marginTop: Spacing.four, gap: Spacing.two }}>
        {OPTS.map((o) => {
          const sel = state[o.key];
          const count = countFor(o.ruleField);
          return (
            <Pressable
              key={o.key}
              onPress={() => setLifestyle(o.key, !sel)}
              style={({ pressed }) => [
                styles.row,
                sel && styles.rowSel,
                pressed && { opacity: 0.92 },
              ]}>
              <View style={[styles.iconBubble, { backgroundColor: o.bg }]}>
                <o.Icon size={20} color={o.tint} strokeWidth={1.7} />
              </View>
              <View style={{ flex: 1 }}>
                <View style={styles.titleRow}>
                  <Text style={styles.title}>{o.title}</Text>
                  <View style={[styles.countPill, sel && styles.countPillSel]}>
                    <Text style={[styles.countPillText, sel && styles.countPillTextSel]}>
                      +{count} task{count === 1 ? '' : 's'}
                    </Text>
                  </View>
                </View>
                <Text style={styles.sub}>{o.sub}</Text>
              </View>
              <View style={[styles.toggle, sel && styles.toggleOn]}>
                <View style={[styles.thumb, sel && styles.thumbOn]} />
              </View>
            </Pressable>
          );
        })}
      </View>

      <View style={styles.hazardSection}>
        <View style={styles.hazardHeader}>
          <View style={[styles.iconBubble, { backgroundColor: 'rgba(176,95,60,0.15)' }]}>
            <Wind size={20} color={Palette.clay} strokeWidth={1.7} />
          </View>
          <View style={{ flex: 1 }}>
            <Text style={styles.hazardHeaderTitle}>
              Any of these natural hazards in your area?
            </Text>
            <Text style={styles.hazardHeaderSub}>
              {state.regionHazards.length > 0
                ? 'We pre-checked some based on your ZIP — tap to refine.'
                : 'Pick anything that applies. Each unlocks a storm-prep bundle.'}
            </Text>
          </View>
        </View>
        <View style={styles.hazardChips}>
          {REGION_HAZARD_ORDER.map((h) => {
            const sel = state.regionHazards.includes(h);
            const count = HAZARD_RULE_COUNTS[h];
            return (
              <Pressable
                key={h}
                onPress={() => toggleRegionHazard(h)}
                style={({ pressed }) => [
                  styles.hazardChip,
                  sel && styles.hazardChipSel,
                  pressed && { opacity: 0.85 },
                ]}>
                <Text
                  style={[
                    styles.hazardChipText,
                    sel && styles.hazardChipTextSel,
                  ]}>
                  {REGION_HAZARD_LABEL[h]}
                </Text>
                <Text
                  style={[
                    styles.hazardChipCount,
                    sel && styles.hazardChipCountSel,
                  ]}>
                  +{count}
                </Text>
              </Pressable>
            );
          })}
        </View>
      </View>

      {totalExtra > 0 || state.regionHazards.length > 0 ? (
        <View style={styles.totalBox}>
          <Text style={styles.totalText}>
            Adds <Text style={styles.totalBold}>{totalExtra + state.regionHazards.reduce((s, h) => s + HAZARD_RULE_COUNTS[h], 0)}</Text> reminders to your plan
          </Text>
        </View>
      ) : null}

    </Screen>
  );
}

const styles = StyleSheet.create({
  // Small "Select all" affordance above the lifestyle list. Plain text,
  // honey-tinted, low-effort target. Toggles between "Select all" and
  // "✓ All selected — tap to clear" based on current state.
  selectAll: {
    marginTop: 10,
    alignSelf: 'flex-end',
    paddingVertical: 4,
    paddingHorizontal: 8,
  },
  selectAllText: {
    fontFamily: Fonts.sansBold,
    fontSize: 13,
    color: Palette.honeyDeep,
    letterSpacing: 0.2,
  },
  row: {
    flexDirection: 'row', alignItems: 'center', gap: 12,
    backgroundColor: Palette.card,
    borderWidth: 1.5, borderColor: Palette.line,
    borderRadius: Radius.lg,
    paddingHorizontal: 14, paddingVertical: 13,
  },
  rowSel: { borderColor: Palette.forest, backgroundColor: '#F4F7F2' },
  iconBubble: {
    width: 40, height: 40, borderRadius: 12,
    alignItems: 'center', justifyContent: 'center',
  },
  titleRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  title: { fontFamily: Fonts.sansBold, fontSize: 14.5, color: Palette.ink },
  sub: {
    marginTop: 2,
    fontFamily: Fonts.sansMedium, fontSize: 12.5, color: Palette.inkSoft,
    lineHeight: 16,
  },
  countPill: {
    paddingHorizontal: 7, paddingVertical: 2, borderRadius: 999,
    backgroundColor: Palette.sand,
    borderWidth: 1, borderColor: Palette.line,
  },
  countPillSel: { backgroundColor: Palette.honeySoft, borderColor: 'rgba(225,163,61,0.4)' },
  countPillText: {
    fontFamily: Fonts.sansBold, fontSize: 11, color: Palette.inkFaint, letterSpacing: 0.3,
  },
  countPillTextSel: { color: Palette.honeyDeep },
  hazardSection: {
    marginTop: Spacing.four,
    padding: Spacing.three,
    backgroundColor: Palette.card,
    borderWidth: 1.5,
    borderColor: Palette.line,
    borderRadius: Radius.lg,
  },
  hazardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  hazardHeaderTitle: {
    fontFamily: Fonts.sansBold,
    fontSize: 14.5,
    color: Palette.ink,
  },
  hazardHeaderSub: {
    marginTop: 2,
    fontFamily: Fonts.sansMedium,
    fontSize: 12.5,
    color: Palette.inkSoft,
    lineHeight: 16,
  },
  hazardChips: {
    marginTop: Spacing.three,
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  hazardChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 999,
    borderWidth: 1.5,
    borderColor: Palette.line,
    backgroundColor: Palette.sand,
  },
  hazardChipSel: {
    backgroundColor: Palette.forest,
    borderColor: Palette.forest,
  },
  hazardChipText: {
    fontFamily: Fonts.sansBold,
    fontSize: 13.5,
    color: Palette.ink,
  },
  hazardChipTextSel: {
    color: Palette.cream,
  },
  hazardChipCount: {
    fontFamily: Fonts.sansBold,
    fontSize: 11.5,
    color: Palette.inkFaint,
    letterSpacing: 0.3,
  },
  hazardChipCountSel: {
    color: 'rgba(251,246,234,0.85)',
  },
  totalBox: {
    marginTop: Spacing.three,
    padding: 12,
    backgroundColor: '#EAF0EA',
    borderRadius: 12,
    alignItems: 'center',
  },
  totalText: {
    fontFamily: Fonts.sansSemibold, fontSize: 13.5, color: Palette.inkSoft,
  },
  totalBold: { fontFamily: Fonts.sansBold, color: Palette.forest },
  toggle: {
    width: 44, height: 26, borderRadius: 13,
    backgroundColor: Palette.sandDeep, padding: 3, justifyContent: 'center',
  },
  toggleOn: { backgroundColor: Palette.forest },
  thumb: { width: 20, height: 20, borderRadius: 10, backgroundColor: Palette.cream },
  thumbOn: { transform: [{ translateX: 18 }] },
});
